/**
 * @fileoverview Opt-in empty state for a job with no mailbox yet (MOR-529),
 * with a user-chosen address local part (MOR-783).
 *
 * The prefill is the slugified job name (falling back to the entity name,
 * matching how jobs are titled elsewhere); whatever the user submits (edited
 * or not) is provisioned verbatim, so the address is exactly what they saw.
 * Validation mirrors convex/mail/handleRules.ts.
 */

import { useEffect, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { Mail } from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { extractErrorMessage } from "@/utils/errors";
import { validateHandleClient } from "./EnableMailboxCard.helpers";

/** Mirrors slugifyHandle in convex/mail/mutations.ts. */
function slugify(input: string): string {
  return (
    input
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 30)
      .replace(/-+$/, "") || "job"
  );
}

export function MailboxAddressField({
  handle,
  onChange,
  error,
  domain,
}: {
  handle: string;
  onChange: (next: string) => void;
  error: string | null;
  /** Fixed domain every new address lands on (`MAIL_PROVIDER=ses`). */
  domain?: string | null;
}) {
  return (
    <div className="w-full space-y-1.5 text-left">
      <Label htmlFor="mailbox-handle">Email address</Label>
      <Input
        id="mailbox-handle"
        value={handle}
        onChange={(e) => onChange(e.target.value.toLowerCase())}
        autoComplete="off"
        spellCheck={false}
      />
      <p className="text-xs text-muted-foreground">
        {domain
          ? `This is the part before the @. The address will end in @${domain}.`
          : "This is the part before the @. The domain comes from your workspace's sending domain settings."}
      </p>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

export function EnableMailboxCard({ jobId }: { jobId: Id<"jobs"> }) {
  const enableMailbox = useMutation(api.mail.mutations.enableMailbox);
  const job = useQuery(api.jobs.queries.getJob, { jobId }) as
    { jobName?: string; entityName: string } | null | undefined;
  const mailInfo = useQuery(api.mail.queries.getMailProviderInfo, { jobId });

  const [handle, setHandle] = useState("");
  const [touched, setTouched] = useState(false);
  const [enabling, setEnabling] = useState(false);

  // Prefill once the job loads (don't clobber a user edit).
  useEffect(() => {
    if (!job || touched || handle.length > 0) return;
    setHandle(slugify(job.jobName || job.entityName));
  }, [job, touched, handle]);

  const validation = handle.length > 0 ? validateHandleClient(handle) : null;

  const handleEnable = async () => {
    const error = validateHandleClient(handle);
    if (error) {
      toast.error(error);
      return;
    }
    setEnabling(true);
    try {
      await enableMailbox({ jobId, customHandle: handle });
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to enable mailbox"));
      setEnabling(false);
    }
    // On success the parent query flips to "provisioning" and unmounts this
    // card, so we leave `enabling` set to keep the button busy until then.
  };

  return (
    <div className="flex flex-1 items-center justify-center bg-muted p-6">
      <div className="flex max-w-md flex-col items-center gap-4 rounded-lg border bg-white p-8 text-center shadow-sm dark:bg-card">
        <div className="flex size-12 items-center justify-center rounded-full bg-primary/10">
          <Mail className="size-6 text-primary" />
        </div>
        <div className="space-y-1.5">
          <h2 className="text-lg font-semibold text-primary">
            Enable a mailbox for this job
          </h2>
          <p className="text-sm text-muted-foreground">
            The job gets its own email address managed by Morgan. Inbound mail
            lands here, and Morgan can draft replies for you to review before
            anything is sent.
          </p>
        </div>
        <MailboxAddressField
          handle={handle}
          onChange={(next) => {
            setTouched(true);
            setHandle(next);
          }}
          error={touched ? validation : null}
          domain={mailInfo?.provider === "ses" ? mailInfo.domain : null}
        />
        <Button
          onClick={handleEnable}
          disabled={enabling || handle.length === 0 || validation !== null}
        >
          {enabling ? "Enabling..." : "Enable mailbox"}
        </Button>
      </div>
    </div>
  );
}
